'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

export interface SpectrumStop {
  /** Position of this color stop along the spectrum (0–100, as a percent of [min, max]). */
  at: number;
  /** Color at this stop — either a theme palette key or a CSS color string. */
  color: 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary' | (string & {});
}

export interface SpectrumBarProps {
  /** Current value position on the spectrum. */
  value: number;
  /** Minimum of the spectrum range. Default 0. */
  min?: number;
  /** Maximum of the spectrum range. Default 100. */
  max?: number;
  /**
   * Color stops along the gradient. Default: error → warning → success (credit-score style).
   * Stops should be ordered ascending by `at`.
   */
  stops?: SpectrumStop[];
  /** Optional scale labels rendered below the bar (e.g. ["300", "580", "670", "740", "850"]). */
  scaleLabels?: ReactNode[];
  /** Bar height. Default 6. */
  height?: number;
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
  /** Aria label. */
  ariaLabel?: string;
}

const DEFAULT_STOPS: SpectrumStop[] = [
  { at: 0, color: 'error' },
  { at: 50, color: 'warning' },
  { at: 100, color: 'success' },
];

type PaletteKey = 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary';

const PALETTE_KEYS: ReadonlySet<string> = new Set<PaletteKey>([
  'success',
  'warning',
  'error',
  'info',
  'primary',
  'secondary',
]);

function resolveColor(theme: Theme, color: SpectrumStop['color']): string {
  if (typeof color === 'string' && PALETTE_KEYS.has(color)) {
    return theme.palette[color as PaletteKey].main;
  }
  return color;
}

export function SpectrumBar({
  value,
  min = 0,
  max = 100,
  stops = DEFAULT_STOPS,
  scaleLabels,
  height = 6,
  sx,
  ariaLabel,
}: SpectrumBarProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const span = max - min || 1;
  const clamped = Math.min(max, Math.max(min, value));
  const percent = ((clamped - min) / span) * 100;

  // Empty `stops` would yield an invalid `linear-gradient(90deg, )` string; fall back to defaults.
  const effectiveStops = stops.length > 0 ? stops : DEFAULT_STOPS;
  const gradient = `linear-gradient(90deg, ${effectiveStops.map((s) => `${resolveColor(theme, s.color)} ${s.at}%`).join(', ')})`;

  const tickBg = isDark ? '#fff' : theme.palette.text.primary;
  const tickRing = isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)';

  return (
    <Box sx={sx}>
      <Box
        // Without an accessible name a screen reader announces an anonymous
        // value — mark the bar `aria-hidden` instead so AT skips it. The
        // numeric tick + scale labels remain visible for sighted users.
        {...(ariaLabel
          ? {
              role: 'progressbar' as const,
              'aria-valuenow': clamped,
              'aria-valuemin': min,
              'aria-valuemax': max,
              'aria-label': ariaLabel,
            }
          : { 'aria-hidden': true })}
        sx={{
          position: 'relative',
          height,
          borderRadius: height / 2,
          background: gradient,
          opacity: isDark ? 0.85 : 1,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: -3,
            left: `${percent}%`,
            transform: 'translateX(-50%)',
            width: 12,
            height: height + 6,
            borderRadius: 1,
            backgroundColor: tickBg,
            border: `2px solid ${tickRing}`,
            boxShadow: `0 0 0 1px ${alpha(theme.palette.background.default, 0.5)}`,
          }}
        />
      </Box>
      {scaleLabels && scaleLabels.length > 0 && (
        <Box
          aria-hidden
          sx={{
            mt: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {scaleLabels.map((label, index) => (
            <Typography
              key={index}
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.6875rem',
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

SpectrumBar.displayName = 'SpectrumBar';

export default SpectrumBar;
